import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  isTheme,
  resolveIsDark,
  type Theme,
  THEME_STORAGE_KEY,
  ThemeContext,
} from "./theme-context";

const DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)";

/**
 * `dark` クラスを documentElement に付け外しする（Tailwind 4 の
 * @custom-variant dark に対応）。初回描画のちらつきは index.html の
 * インラインスクリプトが先にクラスを当てて防いでいる。
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);

  useEffect(() => {
    const media = window.matchMedia(DARK_MEDIA_QUERY);

    const apply = () => {
      document.documentElement.classList.toggle(
        "dark",
        resolveIsDark(theme, media.matches),
      );
    };

    apply();
    // system のときだけ OS 設定の変化に追従する
    if (theme !== "system") return;

    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // プライベートブラウジング等で localStorage が使えなくても動作は継続する
    }
  }, []);

  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(stored) ? stored : "system";
  } catch {
    return "system";
  }
}
